import { useMemo, useState, useCallback } from 'react';
import { ImagePreviewOverlay } from '../ImagePreviewOverlay';
import { useIsMobile } from '../ConciergeChatAreaV2/useIsMobile';
import type { ConciergeMessagePart } from '../../types/conciergeToolTypes';
import { TextPart } from './TextPart';
import { isCallState, isOutputState } from './types';
import { FilterResultsPart } from './FilterResultsPart';
import { SurveysPart } from './SurveysPart';
import { getMergedStoreLocatorInfo } from './StoreLocatorPart';
import { ProductDetailsPart } from './ProductDetailsPart';

import { PdpProductInfoPart } from './PdpProductInfoPart';
import { PromptLoginPart } from './PromptLoginPart';
import { MfWidgetLoader } from './MfWidgetLoader';
import {
  SupportTicketCardFromCreateTicket,
  SupportTicketCardFromRouteToSupport,
} from './SupportTicketCard';
import { SupportTicketsListPart } from './SupportTicketsListPart';
import { SupportTicketStatusPart } from './SupportTicketStatusPart';
import { HumanReplyPart } from './HumanReplyPart';
import { VoiceWidgetInTextChat } from '../VoiceWidgetInTextChat';

// Plan 7b — voice-persisted tool-result parts carry a different `output`
// shape than text-mode tool-result parts (`{ narrator, durationMs,
// endCallAfterMs, status }` per persistTranscriptsFromEvents). The widget
// + the assistant transcript already convey what happened, so we suppress
// these in the text chat replay.
function isVoiceToolResultOutput(output: unknown): boolean {
  if (!output || typeof output !== 'object') return false;
  const o = output as Record<string, unknown>;
  return typeof o.narrator !== 'undefined' && typeof o.durationMs !== 'undefined';
}

type SourceUrlPart = Extract<ConciergeMessagePart, { type: 'source-url' }>;
type DataPromptLoginPart = Extract<ConciergeMessagePart, { type: 'data-prompt-login' }>;
type PromptLoginToolPart = Extract<ConciergeMessagePart, { type: 'tool-prompt_login' }>;
type DataPdpProductInfoPart = Extract<ConciergeMessagePart, { type: 'data-pdp-product-info' }>;

export function ConciergePartItem({
  part,
  allParts,
  onProductClick,
  isStreaming,
  pdpStacklineSku,
  isOnChatWidget,
  apiBaseUrl,
}: {
  part: ConciergeMessagePart;
  allParts: ConciergeMessagePart[];
  onProductClick?: (sku: string) => void;
  isStreaming?: boolean;
  pdpStacklineSku?: string | null;
  isOnChatWidget?: boolean;
  apiBaseUrl?: string;
}) {
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const closePreview = useCallback(() => setPreviewImageUrl(null), []);
  const isMobile = useIsMobile();

  const sourcesMap = useMemo(() => {
    const map = new Map<string, { url: string; title?: string }>();
    allParts.forEach((p) => {
      if (p.type === 'source-url') {
        const sp = p as SourceUrlPart;
        map.set(String(sp.sourceId), { url: sp.url, title: sp.title });
      } else if (p.type === 'data-source-url') {
        const dp = (p as any).data;
        if (dp?.sourceId) map.set(String(dp.sourceId), { url: dp.url, title: dp.title });
      }
    });
    return map;
  }, [allParts]);

  // Plan 7b — voice-persisted `data-widget-*` parts (other than the
  // ephemeral `tool-running` / `clear` cues) dispatch to the
  // VoiceWidgetInTextChat adapter, which reuses the voice widget
  // components wrapped in <VoiceThemeProvider>.
  if (
    typeof part.type === 'string' &&
    part.type.startsWith('data-widget-') &&
    part.type !== 'data-widget-tool-running' &&
    part.type !== 'data-widget-clear'
  ) {
    return <VoiceWidgetInTextChat type={part.type} data={(part as any).data} />;
  }

  // Plan 7b — suppress voice-shape tool-* parts. The adjacent assistant
  // transcript + the data-widget-* part already render the result; the
  // narrator/duration object has no useful surface in the text chat.
  if (
    typeof part.type === 'string' &&
    part.type.startsWith('tool-') &&
    isVoiceToolResultOutput((part as any).output)
  ) {
    return null;
  }

  switch (part.type) {
    case 'file':
      if (part.mediaType?.startsWith('image/') && part.url) {
        const imageCount = allParts.filter(
          (p) => p.type === 'file' && (p as any).mediaType?.startsWith('image/'),
        ).length;
        const imgSize = imageCount === 1 ? (isMobile ? 250 : 256) : (isMobile ? 96 : 128);
        return (
          <>
            <div className="tw:flex tw:justify-end tw:w-full tw:mb-2">
              <div
                onClick={() => setPreviewImageUrl(part.url!)}
                style={{
                  width: `${imgSize}px`,
                  height: `${imgSize}px`,
                  borderRadius: '12px',
                  overflow: 'hidden',
                  backgroundColor: '#f9f9f9',
                  cursor: 'zoom-in',
                }}
              >
                <img
                  src={part.url}
                  alt={part.filename || 'Uploaded image'}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    mixBlendMode: 'multiply',
                  }}
                />
              </div>
            </div>
            {previewImageUrl && <ImagePreviewOverlay src={previewImageUrl} onClose={closePreview} />}
          </>
        );
      }
      if (part.url) {
        return (
          <a
            href={part.url}
            target="_blank"
            rel="noopener noreferrer"
            className="tw:text-sm tw:underline tw:text-blue-600"
          >
            {part.filename || 'Attached file'}
          </a>
        );
      }
      return null;

    case 'text':
      return <TextPart text={part.text || ''} sourcesMap={sourcesMap} />;

    case 'tool-analyze_query':
    case 'tool-search_products':
      return null;

    case 'tool-filter_results':
      return <FilterResultsPart part={part} />;

    case 'tool-product_carousels':
    case 'tool-organize_results': {
      if (allParts.some((p) => p.type === 'data-product-carousels' || p.type === 'data-section')) return null;
      if (isCallState(part.state) && isStreaming) return null;
      return (
        <MfWidgetLoader
          widgetId="product-carousels"
          toolOutput={(part as any).output ?? null}
          partState={part.state}
        />
      );
    }

    case 'data-product-carousels':
    case 'data-section':
      return (
        <MfWidgetLoader
          widgetId="product-carousels"
          dataParts={[{ type: part.type, data: (part as any).data }]}
        />
      );

    case 'tool-get_surveys':
      return <SurveysPart part={part} allParts={allParts} />;

    case 'tool-show_store_locator': {
      const mergedInfo = getMergedStoreLocatorInfo(allParts);
      if (mergedInfo) {
        const partIndex = allParts.indexOf(part);
        if (partIndex !== mergedInfo.renderIndex) return null;
      }
      if (isCallState(part.state) && isStreaming) return null;
      const slOutput = (part as any).output;
      if (!slOutput || slOutput.error || slOutput.isOnlineOnly) return null;
      const skus = mergedInfo?.allSkus ?? slOutput?.stacklineSkus ?? [];
      if (!slOutput.hasStores || skus.length === 0) return null;
      return (
        <MfWidgetLoader
          widgetId="store-locator"
          toolInput={(part as any).input ?? null}
          toolOutput={{ ...slOutput, stacklineSkus: skus }}
          partState={part.state}
        />
      );
    }

    case 'tool-get_product_details':
      if (part.input?.silent) return null;
      return <ProductDetailsPart part={part} onProductClick={onProductClick} pdpStacklineSku={pdpStacklineSku} />;

    case 'tool-compare_products': {
      if (isCallState(part.state) && isStreaming) return null;
      const cpOutput = (part as any).output;
      if (!cpOutput?.products?.length || cpOutput.products.length < 2) return null;
      return (
        <MfWidgetLoader
          widgetId="compare-products"
          toolInput={(part as any).input ?? null}
          toolOutput={cpOutput}
          partState={part.state}
        />
      );
    }

    case 'data-product-highlight':
      // No onProductClick: the highlight's "View product" opens the in-app
      // detail panel (host routes navigate_to_product → openProductPanel),
      // matching the carousel rather than navigating to a PDP.
      return (
        <MfWidgetLoader
          widgetId="product-highlight"
          toolOutput={(part as any).data ?? null}
          dataParts={[{ type: 'data-product-highlight', data: (part as any).data }]}
        />
      );

    case 'data-pdp-product-info':
      if (isOnChatWidget && (part as any).data?.autoInjected) return null;
      return <PdpProductInfoPart part={part as DataPdpProductInfoPart} />;

    case 'source-url':
    case 'data-source-url':
      return null; // rendered inline in text via sourcesMap

    case 'data-prompt-login':
      return (
        <PromptLoginPart
          reason={(part as DataPromptLoginPart).data?.reason}
          loginMethod={(part as DataPromptLoginPart).data?.loginMethod}
          isStreaming={isStreaming}
        />
      );

    case 'tool-prompt_login':
      // data-prompt-login is always emitted before prompt_login is invoked; suppress duplicate
      if (allParts.some((p) => p.type === 'data-prompt-login')) return null;
      // No loginMethod on the tool input — PromptLoginPart's isBackRoom()
      // defensive gate still guarantees no phone UI on-store (Plan 7 C3).
      return <PromptLoginPart reason={(part as PromptLoginToolPart).input?.reason} isStreaming={isStreaming} />;

    case 'tool-show_order_history': {
      if (isCallState(part.state) && isStreaming) return null;
      if (!isOutputState(part.state) && isStreaming) return null;
      const ohOutput = (part as any).output;
      if (!ohOutput?._metadata?.isAuthenticated) return null;
      return (
        <MfWidgetLoader
          widgetId="order-history"
          toolInput={(part as any).input ?? null}
          toolOutput={ohOutput}
          partState={part.state}
        />
      );
    }

    case 'tool-route_to_support': {
      // The route_to_support output may be the legacy email/contact-referral
      // text, OR a SUCCESS string from the live_support branch with a ticket id.
      // SupportTicketCardFromRouteToSupport renders only when it detects a
      // ticket reference — otherwise nothing (the supervisor's text reply
      // already carries the message).
      return <SupportTicketCardFromRouteToSupport output={(part as any).output} />;
    }
    case 'tool-create_ticket':
      return <SupportTicketCardFromCreateTicket output={(part as any).output} />;
    case 'tool-request_live_agent':
      // The takeover-started SSE event drives the banner; the tool's text is
      // surfaced as the assistant's greeting reply.
      return null;
    case 'tool-end_live_agent':
      return null;
    case 'tool-search_related_tickets':
    case 'tool-lookup_tickets':
      return <SupportTicketsListPart output={(part as any).output} />;
    case 'tool-get_ticket_status':
      return <SupportTicketStatusPart output={(part as any).output} />;
    case 'tool-delegate_to_support_agent':
      // Subagent's parts (text + tool-results) flow through this part's stream
      // independently — the FE renders them via their own part types. The
      // delegation tool itself has no card.
      return null;
    case 'data-human-reply':
      return <HumanReplyPart data={(part as any).data} />;
    case 'data-live-takeover-active':
    case 'data-live-takeover-started':
    case 'data-live-takeover-ended':
    case 'data-takeover-team-changed':
    case 'data-user-message-mirrored':
    case 'data-user-message-delivery-failed':
    case 'data-ticket-status-updated':
      // Driven via useLiveTakeoverState() at the chat-area level OR rendered
      // inline next to the user message bubble in ChatMessage; no part-level render.
      return null;
    case 'tool-search_review_insights': {
      if (isCallState(part.state) && isStreaming) return null;
      const riWidgetData = (part as any).output?.widgetData;
      if (!riWidgetData?.searches?.length) return null;
      return <VoiceWidgetInTextChat type="data-widget-review-insights" data={riWidgetData} />;
    }

    case 'tool-search_faq':
    case 'tool-escalate_to_support':
    case 'tool-ask_user_for_information':
    case 'tool-query_knowledge_base':
      return null;

    case 'tool-invocation': {
      const ti = (part as any).toolInvocation;
      if (!ti?.toolName) return null;
      const expanded = {
        type: `tool-${ti.toolName}`,
        state: ti.state,
        toolCallId: ti.toolCallId,
        toolName: ti.toolName,
        input: ti.args,
        output: ti.result,
      } as any;
      return (
        <ConciergePartItem
          part={expanded}
          allParts={allParts}
          onProductClick={onProductClick}
          isStreaming={isStreaming}
          pdpStacklineSku={pdpStacklineSku}
          isOnChatWidget={isOnChatWidget}
          apiBaseUrl={apiBaseUrl}
        />
      );
    }

    default:
      return null;
  }
}
